import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import Sidebar from "../../components/Sidebar";
import { AppHeader } from "../../components/layout/AppHeader";
import { CredentialRequiredModal } from "../../components/CredentialRequiredModal";
import { useRole } from "../../hooks/useRole";
import { PatientAPI, searchPatients } from "../../services/patient.service";
import { DoctorAPI } from "../../services/doctor.service";
import { OnboardingAPI } from "../../services/onboarding.service";
import { AppointmentAPI } from "../../services/appointment.service";
import { DashboardAPI } from "../../services/dashboard.service";
import { DiagnosisAPI } from "../../services/diagnosis.service";
import type { PatientCreateInput, PatientDbRecord } from "../../schemas/patient.schema";
import type { DoctorListItem } from "../../services/doctor.service";
import type { AppointmentListItem, AppointmentSlot } from "../../services/appointment.service";
import type { PendingVisit } from "../../services/dashboard.service";
import { db } from "../../db/schema";
import { unwrapFromStorage } from "../../db/encryption-bridge";
import { getPatientAge } from "../../utils/age";
import { isPermissionDenied } from "../../utils/network";

// ── Helpers ─────────────────────────────────────────────────────────────────────
const pad = (n: number) => String(n).padStart(2, "0");

const toDateStr = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const fmtLongDate = (d: Date) =>
  d.toLocaleDateString("en-IN", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const fmtShortDate = (d: Date) =>
  d.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });

const getClockStr = (d: Date = new Date()) =>
  d.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

const toMinutes = (timeStr: string) => {
  if (!timeStr) return 0;
  const parts = timeStr.slice(0, 5).split(":").map(Number);
  return (parts[0] || 0) * 60 + (parts[1] || 0);
};

const formatTimeFromIsoOrStr = (val?: string | null): string => {
  if (!val) return "—";
  if (val.includes("T")) {
    const d = new Date(val);
    if (!isNaN(d.getTime())) {
      return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
    }
  }
  return val.slice(0, 5);
};

const capitalizeWords = (str?: string | null): string => {
  if (!str) return "";
  return str
    .trim()
    .split(/\s+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ""))
    .join(" ");
};

const formatPhoneNumber = (phone?: string | null): string => {
  if (!phone) return "No phone";
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 10) {
    return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
  }
  if (digits.length === 12 && digits.startsWith("91")) {
    return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
  }
  return phone;
};

const formatPatientAgeGender = (
  gender?: string | null,
  dateOfBirth?: string | null,
  ageYears?: number | null
): string => {
  const parts: string[] = [];
  if (gender) parts.push(gender);
  if (dateOfBirth || (ageYears !== null && ageYears !== undefined)) {
    const age = getPatientAge(dateOfBirth, ageYears);
    parts.push(String(age));
  }
  return parts.join(", ");
};

const calculateWaitMinutes = (arrivedAt?: string | null, slotStart?: string | null): number => {
  const ref = arrivedAt || slotStart;
  if (!ref) return 0;
  if (ref.includes("T")) {
    const d = new Date(ref);
    if (!isNaN(d.getTime())) {
      const diffMs = Date.now() - d.getTime();
      return Math.max(0, Math.floor(diffMs / 60000));
    }
  }
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const refMin = toMinutes(ref);
  return Math.max(0, nowMin - refMin);
};

const VISIT_STEP_LABELS: Record<string, string> = {
  "90sdiagnosis": "Diagnosis pad",
  "soap-diagnosis-pad": "SOAP note",
  "continue-diagnosis": "Prescription",
};

const DOCTOR_COLORS = ["#3b82f6", "#f97316", "#8b5cf6", "#10b981", "#06b6d4", "#ec4899"];

// Status styling for appointments matching appointments suite
const APPOINTMENT_STATUS_STYLES: Record<
  string,
  { label: string; pillBg: string; pillFg: string; pillBorder: string; dot: string; border: string }
> = {
  scheduled: {
    label: "Scheduled",
    pillBg: "bg-[#eff6ff]",
    pillFg: "text-[#1d4ed8]",
    pillBorder: "border-[#bfdbfe]",
    dot: "bg-[#3b82f6]",
    border: "border-l-[#60a5fa]",
  },
  confirmed: {
    label: "Confirmed",
    pillBg: "bg-[#ecfeff]",
    pillFg: "text-[#0e7490]",
    pillBorder: "border-[#a5f3fc]",
    dot: "bg-[#06b6d4]",
    border: "border-l-[#22d3ee]",
  },
  in_progress: {
    label: "In Progress",
    pillBg: "bg-[#fffbeb]",
    pillFg: "text-[#b45309]",
    pillBorder: "border-[#fde68a]",
    dot: "bg-[#f59e0b]",
    border: "border-l-[#fbbf24]",
  },
  completed: {
    label: "Completed",
    pillBg: "bg-[#f0fdf4]",
    pillFg: "text-[#15803d]",
    pillBorder: "border-[#bbf7d0]",
    dot: "bg-[#22c55e]",
    border: "border-l-[#4ade80]",
  },
  cancelled: {
    label: "Cancelled",
    pillBg: "bg-[#f1f5f9]",
    pillFg: "text-[#64748b]",
    pillBorder: "border-[#e2e8f0]",
    dot: "bg-[#94a3b8]",
    border: "border-l-[#cbd5e1]",
  },
  no_show: {
    label: "No Show",
    pillBg: "bg-[#fef2f2]",
    pillFg: "text-[#dc2626]",
    pillBorder: "border-[#fecaca]",
    dot: "bg-[#ef4444]",
    border: "border-l-[#f87171]",
  },
};

type StatusFilterType = "" | "consult" | "waiting" | "upcoming" | "seen" | "unfinished";

// ── Component ───────────────────────────────────────────────────────────────────
const StartVisitPage: React.FC = () => {
  const navigate = useNavigate();

  // Role and credential gate
  const { isDoctor, isAdmin, can_prescribe, isLoaded } = useRole();
  const blockedForCredentials = isLoaded && (isDoctor || isAdmin) && !can_prescribe;

  // Data states
  const [patients, setPatients]                 = useState<PatientDbRecord[]>([]);
  const [searchResults, setSearchResults]       = useState<PatientDbRecord[]>([]);
  const [searching, setSearching]               = useState(false);
  const [loadingPatients, setLoadingPatients]   = useState(true);
  const [patientsDenied, setPatientsDenied]     = useState(false);
  const [doctors, setDoctors]                   = useState<DoctorListItem[]>([]);
  const [appointments, setAppointments]         = useState<AppointmentListItem[]>([]);
  const [slots, setSlots]                       = useState<AppointmentSlot[]>([]);
  const [pendingVisits, setPendingVisits]       = useState<PendingVisit[]>([]);

  // Selection & filter states
  const [selectedDoctorId, setSelectedDoctorId] = useState<string>("");
  const [lockedDoctorId, setLockedDoctorId]     = useState<string | null>(null);
  const [statusFilter, setStatusFilter]         = useState<StatusFilterType>("");
  const [query, setQuery]                       = useState("");
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const [updatedClock, setUpdatedClock]         = useState<string>(getClockStr());

  // Walk-in registration form state
  const [showWalkIn, setShowWalkIn]             = useState(false);
  const [walkInFirst, setWalkInFirst]           = useState("");
  const [walkInLast, setWalkInLast]             = useState("");
  const [walkInAge, setWalkInAge]               = useState("");
  const [walkInGender, setWalkInGender]         = useState<"Male" | "Female" | "Other">("Male");
  const [walkInPhone, setWalkInPhone]           = useState("");
  const [walkInErr, setWalkInErr]               = useState("");
  const [submittingWalkIn, setSubmittingWalkIn] = useState(false);

  // Floating Toast with Undo
  const [toastMessage, setToastMessage]         = useState<string | null>(null);
  const [toastUndoAction, setToastUndoAction]   = useState<(() => void) | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((msg: string, undoFn?: () => void) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToastMessage(msg);
    setToastUndoAction(undoFn ? () => undoFn : null);
    toastTimerRef.current = setTimeout(() => {
      setToastMessage(null);
      setToastUndoAction(null);
    }, undoFn ? 5000 : 3000);
  }, []);

  // ── Load Identity & Scoping ─────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    OnboardingAPI.getMe()
      .then((me) => {
        if (cancelled) return;
        if (me.doctor_id) {
          setLockedDoctorId(me.doctor_id);
          setSelectedDoctorId(me.doctor_id);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Debounced server-side search - the initial getAll() is capped at
  // 50 rows with no guaranteed order (see repository.list_patients), so a
  // patient outside that arbitrary first page is invisible to client-side
  // filtering alone, no matter what's typed. Mirrors the working pattern in
  // GlobalSearchBar.tsx / CreateBillPage.tsx.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const hits = await searchPatients(q, 20);
        if (!cancelled) setSearchResults(hits);
      } catch {
        if (!cancelled) setSearchResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query]);

  // ── Load Doctors ─────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const fetchDoctors = () => {
      DoctorAPI.list({ is_active: true })
        .then((data) => {
          if (cancelled) return;
          setDoctors(data);
          try { localStorage.setItem("kgz_doctors_cache", JSON.stringify(data)); } catch {}
          if (!lockedDoctorId && data.length > 0 && !selectedDoctorId) {
            setSelectedDoctorId(data[0].doctor_id);
          }
        })
        .catch(err => {
          if (cancelled) return;
          // Same rule as the patient fetch: a 403 is not an offline signal, so
          // do not repopulate the picker from the localStorage cache.
          if (isPermissionDenied(err)) return;
          try {
            const cached = localStorage.getItem("kgz_doctors_cache");
            if (cached) {
              const data = JSON.parse(cached) as DoctorListItem[];
              setDoctors(data);
              if (!lockedDoctorId && data.length > 0 && !selectedDoctorId) {
                setSelectedDoctorId(data[0].doctor_id);
              }
            }
          } catch {}
        });
    };
    fetchDoctors();
    window.addEventListener("doctorUpdated", fetchDoctors);
    return () => { cancelled = true; window.removeEventListener("doctorUpdated", fetchDoctors); };
  }, [lockedDoctorId, selectedDoctorId]);

  // ── Load Patients ────────────────────────────────────────────────────────────
  // Fetch patients: API first, Dexie fallback for offline ONLY.
  // A 403 must never reach the fallback.
  const loadPatients = useCallback(async () => {
    let cancelled = false;
    const loadFromDexie = async () => {
      try {
        const currentUsers = await db.users.toArray();
        const clinicId = currentUsers[0]?.clinic_id;
        const rows = clinicId
          ? await db.patients.where("clinic_id").equals(clinicId).toArray()
          : await db.patients.toArray();
        if (rows.length === 0) return;
        const decrypted = await Promise.all(
          rows.map((r) => unwrapFromStorage<PatientDbRecord>(r as unknown as Record<string, unknown>))
        );
        if (!cancelled) setPatients(decrypted);
      } catch { /* ignore */ }
    };

    setLoadingPatients(true);
    try {
      const data = await PatientAPI.getAll();
      if (!cancelled) {
        setPatients(data);
        setPatientsDenied(false);
        setLoadingPatients(false);
      }
    } catch (err) {
      if (cancelled) return;
      if (isPermissionDenied(err)) {
        setPatientsDenied(true);
        setLoadingPatients(false);
        return;
      }
      await loadFromDexie();
      if (!cancelled) setLoadingPatients(false);
    }
  }, []);

  // ── Load Appointments, Slots & Visits ────────────────────────────────────────
  const reloadData = useCallback(async () => {
    const todayStr = toDateStr(new Date());
    const todayIso = new Date(`${todayStr}T00:00:00.000Z`).toISOString();

    try {
      const [apptsRes, slotsRes, pendingRes] = await Promise.all([
        AppointmentAPI.list({ date: todayIso }).catch(() => [] as AppointmentListItem[]),
        AppointmentAPI.listSlots({ date: todayIso }).catch(() => [] as AppointmentSlot[]),
        DashboardAPI.getPendingVisits().catch(() => [] as PendingVisit[]),
      ]);

      setAppointments(apptsRes);
      setSlots(slotsRes);
      setPendingVisits(pendingRes);
      setUpdatedClock(getClockStr(new Date()));
    } catch {
      // Fallback
    }
  }, []);

  useEffect(() => {
    loadPatients();
    reloadData();

    // Auto-refresh every 30 seconds
    const timer = setInterval(() => {
      reloadData();
    }, 30000);

    const onFocus = () => reloadData();
    window.addEventListener("focus", onFocus);

    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, [loadPatients, reloadData]);

  // ── Lookup Maps ─────────────────────────────────────────────────────────────
  const doctorMap = useMemo(() => {
    const m: Record<string, DoctorListItem> = {};
    doctors.forEach((d) => { m[d.doctor_id] = d; });
    return m;
  }, [doctors]);

  const patientMap = useMemo(() => {
    const m: Record<string, PatientDbRecord> = {};
    patients.forEach((p) => {
      const pid = p.patient_id as string | undefined;
      if (pid) m[pid] = p;
    });
    return m;
  }, [patients]);

  const slotMap = useMemo(() => {
    const m: Record<string, AppointmentSlot> = {};
    slots.forEach((s) => { m[s.slot_id] = s; });
    return m;
  }, [slots]);

  // Active doctor scope: if locked to a doctor, always that doctor; otherwise selectedDoctorId
  const activeDoctorId = lockedDoctorId || selectedDoctorId;
  const inScope = useCallback(
    (item: { doctor_id?: string | null }) => {
      if (!activeDoctorId) return true;
      return item.doctor_id === activeDoctorId;
    },
    [activeDoctorId]
  );

  const activeDoctorName = useMemo(() => {
    if (!activeDoctorId) return "All doctors";
    return doctorMap[activeDoctorId]?.name ?? "Doctor";
  }, [activeDoctorId, doctorMap]);

  // ── Enriched Appointments ───────────────────────────────────────────────────
  const todayStr = useMemo(() => toDateStr(new Date()), []);

  const enrichedTodayAppointments = useMemo(() => {
    return appointments
      .filter(inScope)
      .map((a) => {
        const slot = a.slot_id ? slotMap[a.slot_id] : undefined;
        const isWalkIn = a.source === "walk_in" || !a.slot_id;
        const arrivalOrCreatedTime = a.arrived_at
          ? formatTimeFromIsoOrStr(a.arrived_at)
          : a.created_at
          ? formatTimeFromIsoOrStr(a.created_at)
          : "—";
        const slotStartTime = slot?.start_time
          ? formatTimeFromIsoOrStr(slot.start_time)
          : arrivalOrCreatedTime !== "—"
          ? arrivalOrCreatedTime
          : "09:00";
        const slotEndTime = slot?.end_time
          ? formatTimeFromIsoOrStr(slot.end_time)
          : isWalkIn
          ? "Walk-in"
          : "09:15";
        const patient = a.patient_id ? patientMap[a.patient_id] : undefined;
        const patientName = patient
          ? capitalizeWords(`${patient.first_name} ${patient.last_name}`)
          : "Patient";

        return {
          ...a,
          slot_start: slotStartTime,
          slot_end: slotEndTime,
          patient_name: patientName,
          patient,
        };
      })
      .sort((a, b) => a.slot_start.localeCompare(b.slot_start));
  }, [appointments, inScope, slotMap, patientMap]);

  // ── Draft Visits (Pending) ──────────────────────────────────────────────────
  const scopedDraftVisits = useMemo(() => {
    return pendingVisits.filter(inScope);
  }, [pendingVisits, inScope]);

  const draftByApptId = useMemo(() => {
    const map: Record<string, PendingVisit> = {};
    scopedDraftVisits.forEach((v) => {
      const matching = enrichedTodayAppointments.find(
        (a) => a.patient_id === v.patient_id && (a.visit_id === v.visit_id || a.status === "in_progress")
      );
      if (matching) {
        map[matching.appointment_id] = v;
      }
    });
    return map;
  }, [scopedDraftVisits, enrichedTodayAppointments]);

  // In Consult: visits in progress today
  const inConsultVisits = useMemo(() => {
    return scopedDraftVisits
      .filter((v) => {
        const visitDateStr = toDateStr(new Date(v.visit_date || v.created_at));
        return visitDateStr === todayStr;
      })
      .sort((a, b) => (b.visit_date || b.created_at).localeCompare(a.visit_date || a.created_at));
  }, [scopedDraftVisits, todayStr]);

  // Unfinished: drafts from days before today
  const unfinishedVisits = useMemo(() => {
    return scopedDraftVisits
      .filter((v) => {
        const visitDateStr = toDateStr(new Date(v.visit_date || v.created_at));
        return visitDateStr < todayStr;
      })
      .sort((a, b) => (b.visit_date || b.created_at).localeCompare(a.visit_date || a.created_at));
  }, [scopedDraftVisits, todayStr]);

  // Waiting: patients arrived/checked-in at reception or registered walk-ins, not yet in consult
  const waitingAppointments = useMemo(() => {
    return enrichedTodayAppointments
      .filter((a) => {
        if (a.status === "completed" || a.status === "cancelled" || a.status === "no_show") return false;
        const hasActiveDraft = !!draftByApptId[a.appointment_id];
        if (hasActiveDraft) return false;
        return a.status === "in_progress" || !!a.arrived_at || a.source === "walk_in" || !a.slot_id;
      })
      .sort((a, b) => {
        const tA = a.arrived_at || a.created_at || a.slot_start;
        const tB = b.arrived_at || b.created_at || b.slot_start;
        return tA.localeCompare(tB);
      });
  }, [enrichedTodayAppointments, draftByApptId]);

  // Upcoming: scheduled / confirmed appointments with a slot that haven't arrived yet
  const upcomingAppointments = useMemo(() => {
    return enrichedTodayAppointments.filter(
      (a) =>
        (a.status === "scheduled" || a.status === "confirmed") &&
        a.source !== "walk_in" &&
        !!a.slot_id &&
        !a.arrived_at
    );
  }, [enrichedTodayAppointments]);

  const completedAppointments = useMemo(() => {
    return enrichedTodayAppointments.filter((a) => a.status === "completed");
  }, [enrichedTodayAppointments]);

  // ── Stat Counts & Bar (matches Start Visit.dc.html exactly) ──────────────────
  const statsList = useMemo(() => {
    return [
      {
        key: "consult" as const,
        label: "In consult",
        value: inConsultVisits.length,
        fg: "text-[#d97706]",
        border: "border-[#fef3c7]",
        bg: "bg-[#fffbeb]",
        activeBg: "bg-[#d97706] text-white border-[#d97706]",
      },
      {
        key: "waiting" as const,
        label: "Waiting",
        value: waitingAppointments.length,
        fg: "text-[#0891b2]",
        border: "border-[#cffafe]",
        bg: "bg-[#ecfeff]",
        activeBg: "bg-[#0891b2] text-white border-[#0891b2]",
      },
      {
        key: "upcoming" as const,
        label: "Upcoming",
        value: upcomingAppointments.length,
        fg: "text-[#2563eb]",
        border: "border-[#dbeafe]",
        bg: "bg-[#eff6ff]",
        activeBg: "bg-[#2563eb] text-white border-[#2563eb]",
      },
      {
        key: "seen" as const,
        label: "Seen",
        value: completedAppointments.length,
        fg: "text-[#16a34a]",
        border: "border-[#dcfce7]",
        bg: "bg-[#f0fdf4]",
        activeBg: "bg-[#16a34a] text-white border-[#16a34a]",
      },
      {
        key: "unfinished" as const,
        label: "Unfinished",
        value: unfinishedVisits.length,
        fg: "text-[#ea580c]",
        border: "border-[#fed7aa]",
        bg: "bg-[#fff7ed]",
        activeBg: "bg-[#ea580c] text-white border-[#ea580c]",
      },
    ].filter((s) => s.value > 0 || s.key !== "unfinished");
  }, [
    inConsultVisits.length,
    waitingAppointments.length,
    upcomingAppointments.length,
    completedAppointments.length,
    unfinishedVisits.length,
  ]);

  // ── Patient Search & Recent Patients ────────────────────────────────────────
  // A real query searches server-side across the whole clinic (see the
  // debounced effect above). When offline or if search returns empty, fall back
  // to client-side filtering over cached patients. Below 2 chars, show recent patients.
  const filteredPatients = useMemo(() => {
    const q = query.trim();
    if (q.length >= 2) {
      if (searchResults.length > 0) return searchResults;
      const qLower = q.toLowerCase();
      const cleanQ = qLower.replace(/\s/g, "");
      return patients
        .filter((p) => {
          const fullName = `${p.first_name} ${p.last_name}`.toLowerCase();
          const phone = (p.contact_number ?? "").replace(/\s/g, "");
          const disp = (p.display_id ?? "").toLowerCase();
          return fullName.includes(qLower) || phone.includes(cleanQ) || disp.includes(cleanQ);
        })
        .slice(0, 30);
    }
    if (q.length === 1) {
      const qLower = q.toLowerCase();
      return patients
        .filter((p) => `${p.first_name} ${p.last_name}`.toLowerCase().includes(qLower))
        .slice(0, 30);
    }
    // With nothing typed, show recent patients (first 6)
    return patients.slice(0, 6);
  }, [patients, query, searchResults]);

  // ── Actions ─────────────────────────────────────────────────────────────────
  const handleStartVisitForPatient = useCallback(
    async (patient: PatientDbRecord, appt?: AppointmentListItem | null) => {
      const pid = patient.display_id || (patient.patient_id as string);
      const did = activeDoctorId || selectedDoctorId;

      if (!did) {
        toast.warning("Please select a doctor to begin the consultation");
        return;
      }

      // If linked to scheduled/confirmed appointment, mark checked in
      if (appt && (appt.status === "scheduled" || appt.status === "confirmed")) {
        try {
          if (appt.status === "scheduled") {
            await AppointmentAPI.confirm(appt.appointment_id).catch(() => {});
          }
          await AppointmentAPI.checkin(appt.appointment_id).catch(() => {});
        } catch {
          /* proceed even if checkin call errors */
        }
      }

      const pName = capitalizeWords(`${patient.first_name} ${patient.last_name}`);
      showToast(`Visit started for ${pName}`);
      const apptParam = appt ? `&appt_id=${appt.appointment_id}` : "";
      navigate(`/90sdiagnosis?patient_id=${pid}&doctor_id=${did}${apptParam}`);
    },
    [activeDoctorId, selectedDoctorId, navigate, showToast]
  );

  const handleStartVisitFromAppt = useCallback(
    async (appt: AppointmentListItem & { patient?: PatientDbRecord }) => {
      if (appt.patient) {
        return handleStartVisitForPatient(appt.patient, appt);
      }
      if (appt.patient_id) {
        try {
          const pt = await PatientAPI.getById(appt.patient_id);
          return handleStartVisitForPatient(pt, appt);
        } catch {
          const did = activeDoctorId || selectedDoctorId;
          const apptParam = `&appt_id=${appt.appointment_id}`;
          navigate(`/90sdiagnosis?patient_id=${appt.patient_id}&doctor_id=${did}${apptParam}`);
        }
      }
    },
    [handleStartVisitForPatient, activeDoctorId, selectedDoctorId, navigate]
  );

  const handleContinueVisit = useCallback(
    (v: PendingVisit) => {
      const step = v.current_step || "90sdiagnosis";
      navigate(`/${step}?visit_id=${v.visit_id}&patient_id=${v.patient_id}&doctor_id=${v.doctor_id}`);
    },
    [navigate]
  );

  const handleFinalizeVisit = useCallback(
    async (v: PendingVisit) => {
      try {
        await DiagnosisAPI.finalizeVisit(v.visit_id);
        showToast(`Visit finalized for ${capitalizeWords(v.patient_name)}`);
        reloadData();
      } catch (err: any) {
        toast.error(err?.message || "Failed to finalize visit");
      }
    },
    [showToast, reloadData]
  );

  // ── Walk-In Patient Submission ──────────────────────────────────────────────
  const handleRegisterAndStartWalkIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setWalkInErr("");

    const firstName = walkInFirst.trim();
    const lastName = walkInLast.trim();
    const ageNum = Number(walkInAge);
    const digits = walkInPhone.replace(/\D/g, "");

    if (!firstName || !lastName) {
      setWalkInErr("First and last name are required.");
      return;
    }
    if (isNaN(ageNum) || ageNum < 0 || ageNum > 120 || walkInAge === "") {
      setWalkInErr("Enter age in years (0–120).");
      return;
    }
    if (digits.length !== 10) {
      setWalkInErr("Mobile number must be exactly 10 digits.");
      return;
    }

    const did = activeDoctorId || selectedDoctorId;
    if (!did) {
      setWalkInErr("Please select a doctor for this visit.");
      return;
    }

    setSubmittingWalkIn(true);
    try {
      const payload: PatientCreateInput = {
        first_name: firstName,
        last_name: lastName,
        gender: walkInGender,
        age_years: ageNum,
        contact_number: `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`,
      };

      const created = await PatientAPI.create(payload);
      setShowWalkIn(false);
      setWalkInFirst("");
      setWalkInLast("");
      setWalkInAge("");
      setWalkInPhone("");
      setQuery("");

      toast.success(`Patient ${capitalizeWords(`${firstName} ${lastName}`)} registered!`);
      handleStartVisitForPatient(created, null);
    } catch (err: any) {
      setWalkInErr(err?.message || "Failed to register patient");
    } finally {
      setSubmittingWalkIn(false);
    }
  };

  const handleOpenWalkInFromQuery = () => {
    const parts = query.trim().split(/\s+/);
    const isNum = /^\d+$/.test(query.replace(/\s/g, ""));
    setShowWalkIn(true);
    setWalkInFirst(isNum ? "" : parts[0] || "");
    setWalkInLast(isNum ? "" : parts.slice(1).join(" "));
    setWalkInPhone(isNum ? query.replace(/\D/g, "") : "");
    setWalkInErr("");
  };

  // ── Filtered Views for Columns ──────────────────────────────────────────────
  const filteredQueueSections = useMemo(() => {
    const showConsult = !statusFilter || statusFilter === "consult";
    const showWaiting = !statusFilter || statusFilter === "waiting";
    const showUnfinished = !statusFilter || statusFilter === "unfinished";

    const consultList = showConsult ? inConsultVisits : [];
    const waitingList = showWaiting ? waitingAppointments : [];
    const unfinishedList = showUnfinished ? unfinishedVisits : [];

    const totalVisible = consultList.length + waitingList.length + unfinishedList.length;
    return { consultList, waitingList, unfinishedList, totalVisible };
  }, [statusFilter, inConsultVisits, waitingAppointments, unfinishedVisits]);

  const filteredAppointmentsList = useMemo(() => {
    if (!statusFilter) return enrichedTodayAppointments;
    if (statusFilter === "upcoming") {
      return enrichedTodayAppointments.filter(
        (a) =>
          (a.status === "scheduled" || a.status === "confirmed") &&
          a.source !== "walk_in" &&
          !!a.slot_id &&
          !a.arrived_at
      );
    }
    if (statusFilter === "seen") {
      return enrichedTodayAppointments.filter((a) => a.status === "completed");
    }
    if (statusFilter === "waiting") {
      return enrichedTodayAppointments.filter(
        (a) =>
          (a.status === "in_progress" || !!a.arrived_at || a.source === "walk_in" || !a.slot_id) &&
          a.status !== "completed" &&
          a.status !== "cancelled" &&
          a.status !== "no_show" &&
          !draftByApptId[a.appointment_id]
      );
    }
    if (statusFilter === "consult") {
      return enrichedTodayAppointments.filter((a) => !!draftByApptId[a.appointment_id]);
    }
    return enrichedTodayAppointments;
  }, [enrichedTodayAppointments, statusFilter, draftByApptId]);

  // "Now" marker placement in appointments column
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();

  // ── Credential Security Gate ────────────────────────────────────────────────
  if (blockedForCredentials) {
    return <CredentialRequiredModal isOpen onClose={() => navigate("/dashboard")} />;
  }

  const totalApptsCount = enrichedTodayAppointments.filter((a) => a.status !== "cancelled").length;

  return (
    <div className="min-h-screen bg-[#f7f8fc] flex font-sans">
      <style>{`
        @keyframes livePulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.5); }
          70% { box-shadow: 0 0 0 6px rgba(245, 158, 11, 0); }
        }
        @keyframes toastIn {
          from { opacity: 0; transform: translate(-50%, 12px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
      `}</style>
      <Sidebar />

      <div className="flex-1 min-w-0 flex flex-col lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out">
        <AppHeader title="Visits" />

        <div className="max-w-[1440px] mx-auto w-full p-4 sm:p-6 flex flex-col gap-5 pt-16 lg:pt-6">

          {/* ── Top Header & Tab Toggle ─────────────────────────────────────── */}
          <div className="flex items-center justify-between gap-4 flex-wrap mb-[-4px]">
            <div>
              <h1 className="text-[20px] font-bold text-[#1e293b] leading-tight">Visits</h1>
              <p className="text-[14px] text-[#94a3b8] mt-0.5">
                {fmtLongDate(new Date())} · {totalApptsCount} appointment{totalApptsCount !== 1 ? "s" : ""}
                {activeDoctorId ? ` · ${activeDoctorName}` : " · all doctors"}
              </p>
            </div>

            {/* Three-way View Toggle (Start Visit · All Visits · Drafts) */}
            <div
              className="flex items-center bg-[#f1f5f9] p-1 rounded-xl gap-0.5"
              role="tablist"
              aria-label="Visit views"
            >
              <span
                aria-current="page"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-semibold bg-white text-[#1e293b] shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 2v2M5 2v2M5 3H4a2 2 0 00-2 2v4a6 6 0 0012 0V5a2 2 0 00-2-2h-1" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 15a6 6 0 0012 0v-3" />
                  <circle cx="20" cy="10" r="2" />
                </svg>
                Start Visit
              </span>

              <Link
                to="/pending-visits?view=all"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium text-[#64748b] hover:text-[#1e293b] transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14.5 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V7.5L14.5 2z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 2v6h6M16 13H8M16 17H8" />
                </svg>
                All Visits
              </Link>

              <Link
                to="/pending-visits"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium text-[#64748b] hover:text-[#1e293b] transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 20h9M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4Z" />
                </svg>
                Drafts
                {scopedDraftVisits.length > 0 && (
                  <span className="min-w-[18px] h-[18px] px-[5px] rounded-full bg-[#ea580c] text-white text-[11px] font-bold inline-flex items-center justify-center">
                    {scopedDraftVisits.length}
                  </span>
                )}
              </Link>
            </div>
          </div>

          {/* ── Stat Filter Chips Row + Single Doctor Picker ─────────────────── */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex flex-wrap gap-2">
              {statsList.map((st) => {
                const isActive = statusFilter === st.key;
                return (
                  <button
                    key={st.key}
                    type="button"
                    onClick={() => setStatusFilter(isActive ? "" : st.key)}
                    className={`flex items-center gap-2 px-3.5 py-2 rounded-2xl border text-[12px] font-medium cursor-pointer transition-all shadow-sm ${
                      isActive ? st.activeBg : `${st.bg} ${st.fg} ${st.border} hover:shadow`
                    } ${statusFilter && !isActive ? "opacity-55" : "opacity-100"}`}
                  >
                    <span className="text-[18px] font-bold leading-none">{st.value}</span>
                    <span>{st.label}</span>
                    {isActive && (
                      <svg className="w-3.5 h-3.5 stroke-[2.5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Main Multi-Column Layout (Equal Heights on Desktop) ─────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 items-start">

            {/* ── COLUMN 1: Live Queue ─────────────────────────────────────── */}
            <section className="order-1 lg:order-2 xl:order-1 bg-white rounded-2xl border border-[#f1f5f9] shadow-sm overflow-hidden flex flex-col xl:h-[calc(100vh-220px)] xl:min-h-[480px] lg:max-h-[560px]">
              <div className="px-4 py-3 border-b border-[#f1f5f9] flex items-center justify-between gap-2 flex-shrink-0 bg-white">
                <div className="flex items-center gap-2">
                  <h2 className="text-[14px] font-semibold text-[#334155]">Queue</h2>
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#047857] bg-[#ecfdf5] border border-[#a7f3d0] rounded-full px-2 py-0.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#10b981] animate-pulse" />
                    Live
                  </span>
                </div>
                <span className="text-[12px] text-[#94a3b8]">Updated {updatedClock}</span>
              </div>

              <div className="overflow-y-auto flex-1 divide-y divide-[#f1f5f9]">
                {filteredQueueSections.totalVisible === 0 ? (
                  <div className="flex flex-col items-center justify-center p-12 text-center text-[#94a3b8] gap-2 h-full">
                    <svg className="w-10 h-10 opacity-30 stroke-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                    </svg>
                    <p className="text-[13px] text-[#94a3b8] max-w-xs">
                      {statusFilter
                        ? "No patients match this filter"
                        : "No one is waiting. Start a walk-in visit from Find patient."}
                    </p>
                  </div>
                ) : (
                  <>
                    {/* IN CONSULT Section */}
                    {filteredQueueSections.consultList.length > 0 && (
                      <div>
                        <div className="px-4 py-2 bg-[#f8fafc] text-[11px] font-semibold text-[#94a3b8] uppercase tracking-wider border-b border-[#f1f5f9]">
                          In consult · {filteredQueueSections.consultList.length}
                        </div>
                        {filteredQueueSections.consultList.map((v) => {
                          const pt = patientMap[v.patient_id];
                          const patientName = pt ? capitalizeWords(`${pt.first_name} ${pt.last_name}`) : capitalizeWords(v.patient_name) || "Patient";
                          const ageSex = pt ? formatPatientAgeGender(pt.gender, pt.date_of_birth, pt.age_years) : "";
                          const stepLabel = VISIT_STEP_LABELS[v.current_step] || "Diagnosis pad";
                          const startTime = formatTimeFromIsoOrStr(v.visit_date || v.created_at);

                          return (
                            <div
                              key={v.visit_id}
                              className="px-4 py-3 border-l-4 border-l-[#fbbf24] bg-[#fffdf7] hover:bg-[#fff9ed] transition-colors flex items-center gap-3 flex-wrap border-b border-[#f1f5f9]"
                            >
                              <div className="w-8 h-8 rounded-full bg-[#fef3c7] flex items-center justify-center flex-shrink-0">
                                <span className="w-2.5 h-2.5 rounded-full bg-[#f59e0b] [animation:livePulse_1.6s_ease-out_infinite]" />
                              </div>
                              <div className="flex-1 min-w-[140px]">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-[14px] font-semibold text-[#1e293b]">{patientName}</span>
                                  {ageSex && <span className="text-[11px] text-[#94a3b8]">{ageSex}</span>}
                                  {(() => {
                                    const matchingAppt = enrichedTodayAppointments.find(
                                      (a) => a.patient_id === v.patient_id && (a.visit_id === v.visit_id || a.status === "in_progress")
                                    );
                                    const isWalkIn = matchingAppt ? (matchingAppt.source === "walk_in" || !matchingAppt.slot_id) : true;
                                    return isWalkIn ? (
                                      <span className="text-[10px] font-semibold bg-[#f1f5f9] text-[#475569] border border-[#e2e8f0] rounded-full px-[7px] py-[1px]">
                                        Walk-in
                                      </span>
                                    ) : (
                                      <span className="text-[10px] font-semibold bg-[#eff6ff] text-[#1d4ed8] border border-[#bfdbfe] rounded-full px-[7px] py-[1px]">
                                        Appt {matchingAppt?.slot_start}
                                      </span>
                                    );
                                  })()}
                                </div>
                                <div className="text-[12px] text-[#b45309] font-normal mt-0.5">
                                  Started {startTime} · {stepLabel}
                                </div>
                                {v.chief_complaint && (
                                  <div className="text-[12px] text-[#64748b] truncate mt-0.5 max-w-sm">
                                    {v.chief_complaint}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-2 ml-auto flex-shrink-0">
                                <button
                                  type="button"
                                  onClick={() => handleFinalizeVisit(v)}
                                  className="px-3 min-h-[36px] rounded-lg text-[13px] font-semibold text-[#15803d] bg-white border border-[#bbf7d0] hover:bg-[#f0fdf4] transition"
                                >
                                  Finalize
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleContinueVisit(v)}
                                  className="px-3.5 min-h-[36px] rounded-lg text-[13px] font-semibold text-white bg-[#2563eb] hover:opacity-90 transition shadow-sm flex items-center gap-1.5"
                                >
                                  Continue
                                  <svg className="w-3.5 h-3.5 stroke-[2.5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* WAITING Section */}
                    {filteredQueueSections.waitingList.length > 0 && (
                      <div>
                        <div className="px-4 py-2 bg-[#f8fafc] text-[11px] font-semibold text-[#94a3b8] uppercase tracking-wider border-b border-[#f1f5f9]">
                          Waiting · {filteredQueueSections.waitingList.length}
                        </div>
                        {filteredQueueSections.waitingList.map((a, idx) => {
                          const waitMin = calculateWaitMinutes(a.arrived_at || a.created_at, a.slot_start);
                          const isNext = idx === 0;
                          const patient = a.patient;
                          const ageSex = patient ? formatPatientAgeGender(patient.gender, patient.date_of_birth, patient.age_years) : "";
                          const isWalkIn = a.source === "walk_in" || !a.slot_id;

                          return (
                            <div
                              key={a.appointment_id}
                              className="px-4 py-3 border-l-4 border-l-[#22d3ee] bg-white hover:bg-[#f8fafc] transition-colors flex items-center gap-3 flex-wrap border-b border-[#f1f5f9]"
                            >
                              <div
                                className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-[11px] font-bold ${
                                  isNext ? "bg-[#ecfeff] text-[#0e7490]" : "bg-[#f1f5f9] text-[#64748b]"
                                }`}
                              >
                                {idx + 1}
                              </div>
                              <div className="flex-1 min-w-[140px]">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-[14px] font-semibold text-[#1e293b]">{a.patient_name}</span>
                                  {ageSex && <span className="text-[11px] text-[#94a3b8]">{ageSex}</span>}
                                  {isWalkIn && (
                                    <span className="text-[10px] font-semibold bg-[#f1f5f9] text-[#475569] border border-[#e2e8f0] rounded-full px-[7px] py-[1px]">
                                      Walk-in
                                    </span>
                                  )}
                                  {isNext && (
                                    <span className="text-[10px] font-semibold bg-[#ecfeff] text-[#0e7490] border border-[#a5f3fc] rounded-full px-[7px] py-[1px]">
                                      NEXT
                                    </span>
                                  )}
                                </div>
                                <div
                                  className={`text-[12px] mt-0.5 ${
                                    waitMin > 20 ? "text-[#d97706] font-semibold" : "text-[#94a3b8] font-normal"
                                  }`}
                                >
                                  {isWalkIn
                                    ? `Walk-in · ${a.arrived_at ? `arrived ${formatTimeFromIsoOrStr(a.arrived_at)} · ` : ""}waiting ${waitMin} min`
                                    : `Appt ${a.slot_start} · arrived ${formatTimeFromIsoOrStr(a.arrived_at)} · waiting ${waitMin} min`}
                                </div>
                                {a.notes && (
                                  <div className="text-[12px] text-[#64748b] truncate mt-0.5 max-w-sm">
                                    {a.notes}
                                  </div>
                                )}
                              </div>
                              <div className="ml-auto flex-shrink-0">
                                <button
                                  type="button"
                                  onClick={() => handleStartVisitFromAppt(a)}
                                  className="px-3.5 min-h-[36px] rounded-lg text-[13px] font-semibold text-white bg-[#2563eb] hover:opacity-90 transition shadow-sm flex items-center gap-1.5"
                                >
                                  Start Visit
                                  <svg className="w-3.5 h-3.5 stroke-[2.5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* UNFINISHED VISITS Section */}
                    {filteredQueueSections.unfinishedList.length > 0 && (
                      <div>
                        <div className="px-4 py-2 bg-[#f8fafc] text-[11px] font-semibold text-[#94a3b8] uppercase tracking-wider border-b border-[#f1f5f9]">
                          Unfinished visits · {filteredQueueSections.unfinishedList.length}
                        </div>
                        {filteredQueueSections.unfinishedList.map((v) => {
                          const pt = patientMap[v.patient_id];
                          const patientName = pt ? capitalizeWords(`${pt.first_name} ${pt.last_name}`) : capitalizeWords(v.patient_name) || "Patient";
                          const ageSex = pt ? formatPatientAgeGender(pt.gender, pt.date_of_birth, pt.age_years) : "";
                          const stepLabel = VISIT_STEP_LABELS[v.current_step] || "Diagnosis pad";
                          const vDate = new Date(v.visit_date || v.created_at);

                          return (
                            <div
                              key={v.visit_id}
                              className="px-4 py-3 border-l-4 border-l-[#fdba74] bg-white hover:bg-[#f8fafc] transition-colors flex items-center gap-3 flex-wrap border-b border-[#f1f5f9]"
                            >
                              <div className="w-8 h-8 rounded-full bg-[#fff7ed] text-[#c2410c] border border-[#fed7aa] flex items-center justify-center flex-shrink-0 text-[11px] font-bold">
                                {vDate.getDate()}
                              </div>
                              <div className="flex-1 min-w-[140px]">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-[14px] font-semibold text-[#1e293b]">{patientName}</span>
                                  {ageSex && <span className="text-[11px] text-[#94a3b8]">{ageSex}</span>}
                                  <span className="text-[10px] font-semibold bg-[#ffedd5] text-[#c2410c] border border-[#fed7aa] rounded-full px-[7px] py-[1px]">
                                    Draft
                                  </span>
                                </div>
                                <div className="text-[12px] text-[#94a3b8] mt-0.5">
                                  {fmtShortDate(vDate)}, {formatTimeFromIsoOrStr(v.visit_date || v.created_at)} · stopped at {stepLabel}
                                </div>
                                {v.chief_complaint && (
                                  <div className="text-[12px] text-[#64748b] truncate mt-0.5 max-w-sm">
                                    {v.chief_complaint}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-2 ml-auto flex-shrink-0">
                                <button
                                  type="button"
                                  onClick={() => handleFinalizeVisit(v)}
                                  className="px-3 min-h-[36px] rounded-lg text-[13px] font-semibold text-[#15803d] bg-white border border-[#bbf7d0] hover:bg-[#f0fdf4] transition"
                                >
                                  Finalize
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleContinueVisit(v)}
                                  className="px-3.5 min-h-[36px] rounded-lg text-[13px] font-semibold text-white bg-[#ea580c] hover:opacity-90 transition shadow-sm flex items-center gap-1.5"
                                >
                                  Resume
                                  <svg className="w-3.5 h-3.5 stroke-[2.5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            </section>

            {/* ── COLUMN 2: Today's Appointments ─────────────────────────── */}
            <section className="order-2 lg:order-3 xl:order-2 bg-white rounded-2xl border border-[#f1f5f9] shadow-sm overflow-hidden flex flex-col xl:h-[calc(100vh-220px)] xl:min-h-[480px] lg:max-h-[560px]">
              <div className="px-4 py-3 border-b border-[#f1f5f9] flex items-center justify-between gap-2 flex-shrink-0 bg-white">
                <h2 className="text-[14px] font-semibold text-[#334155]">
                  Today's appointments · {enrichedTodayAppointments.length}
                </h2>
                <Link
                  to="/appointments"
                  className="text-[12px] font-medium text-[#0891b2] hover:text-[#0e7490] transition"
                >
                  Open schedule
                </Link>
              </div>

              <div className="overflow-y-auto flex-1 divide-y divide-[#f1f5f9]">
                {filteredAppointmentsList.length === 0 ? (
                  <div className="flex flex-col items-center justify-center p-12 text-center text-[#94a3b8] gap-2 h-full">
                    <svg className="w-10 h-10 opacity-30 stroke-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <rect x="3" y="4" width="18" height="18" rx="2" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16 2v4M8 2v4M3 10h18" />
                    </svg>
                    <p className="text-[13px] text-[#94a3b8]">
                      {statusFilter ? "No appointments match this filter" : "No appointments today"}
                    </p>
                  </div>
                ) : (
                  (() => {
                    let nowDividerPlaced = false;
                    const items: React.ReactNode[] = [];

                    filteredAppointmentsList.forEach((appt) => {
                      const apptStartMin = toMinutes(appt.slot_start);

                      // Place "Now HH:MM" divider line before the first future appointment
                      if (!nowDividerPlaced && !statusFilter && apptStartMin > nowMin) {
                        items.push(
                          <div key="now-divider" className="flex items-center gap-2 px-4 py-1.5">
                            <span className="text-[11px] font-bold text-[#ef4444]">
                              Now {updatedClock}
                            </span>
                            <span className="flex-1 h-px bg-[#fecaca]" />
                          </div>
                        );
                        nowDividerPlaced = true;
                      }

                      const hasDraft = !!draftByApptId[appt.appointment_id];
                      const inClinic = appt.status === "in_progress";
                      const isWalkIn = appt.source === "walk_in" || !appt.slot_id;
                      const isWaiting = (inClinic || !!appt.arrived_at || isWalkIn) && !hasDraft && appt.status !== "completed" && appt.status !== "cancelled" && appt.status !== "no_show";
                      const canStart = (appt.status === "scheduled" || appt.status === "confirmed" || inClinic || isWalkIn) && !hasDraft && appt.status !== "completed" && appt.status !== "cancelled" && appt.status !== "no_show";
                      const isCompleted = appt.status === "completed";
                      const isCancelled = appt.status === "cancelled" || appt.status === "no_show";

                      // Determine pill styling
                      const pillCfg = inClinic
                        ? hasDraft
                          ? APPOINTMENT_STATUS_STYLES["in_progress"]
                          : APPOINTMENT_STATUS_STYLES["confirmed"]
                        : isWaiting
                        ? APPOINTMENT_STATUS_STYLES["confirmed"]
                        : APPOINTMENT_STATUS_STYLES[appt.status] || APPOINTMENT_STATUS_STYLES["scheduled"];

                      const pillLabel = inClinic
                        ? hasDraft
                          ? "In consult"
                          : "Waiting"
                        : isWaiting
                        ? "Waiting"
                        : pillCfg.label;

                      items.push(
                        <div
                          key={appt.appointment_id}
                          className={`px-4 py-2.5 flex items-center gap-3 hover:bg-[#fafbfd] transition-colors border-l-4 border-b border-[#f8fafc] ${pillCfg.border} ${
                            isCancelled ? "opacity-50" : isCompleted ? "opacity-70" : "opacity-100"
                          }`}
                        >
                          {/* Time badge */}
                          <div className="w-12 flex-shrink-0 text-left">
                            <div className="text-[14px] font-bold text-[#1e293b] leading-tight">
                              {appt.slot_start}
                            </div>
                            <div className="text-[11px] text-[#94a3b8] mt-0.5">{appt.slot_end}</div>
                          </div>

                          {/* Patient info & pill */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[14px] font-semibold text-[#1e293b] truncate">
                                {appt.patient_name}
                              </span>
                              {isWalkIn && (
                                <span className="text-[10px] font-semibold bg-[#f1f5f9] text-[#475569] border border-[#e2e8f0] rounded-full px-[6px] py-[0.5px]">
                                  Walk-in
                                </span>
                              )}
                              <span
                                className={`inline-flex items-center gap-[5px] rounded-full text-[10px] font-medium px-[7px] py-[1px] border ${pillCfg.pillBg} ${pillCfg.pillFg} ${pillCfg.pillBorder}`}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full ${pillCfg.dot}`} />
                                {pillLabel}
                              </span>
                            </div>
                            <div className="text-[12px] text-[#94a3b8] truncate mt-0.5">
                              {appt.notes || (appt.patient ? formatPatientAgeGender(appt.patient.gender, appt.patient.date_of_birth, appt.patient.age_years) || "General consultation" : "General consultation")}
                              {!activeDoctorId && doctorMap[appt.doctor_id] ? ` · ${doctorMap[appt.doctor_id].name}` : ""}
                            </div>
                          </div>

                          {/* Action or status note */}
                          <div className="flex-shrink-0 ml-auto">
                            {canStart && (
                              <button
                                type="button"
                                onClick={() => handleStartVisitFromAppt(appt)}
                                className="px-3 min-h-[34px] rounded-lg text-[12px] font-semibold text-[#2563eb] bg-white border border-[#bfdbfe] hover:bg-[#eff6ff] transition"
                              >
                                Start
                              </button>
                            )}
                            {hasDraft && draftByApptId[appt.appointment_id] && (
                              <button
                                type="button"
                                onClick={() => handleContinueVisit(draftByApptId[appt.appointment_id])}
                                className="px-3 min-h-[34px] rounded-lg text-[12px] font-semibold text-white bg-[#ea580c] hover:opacity-90 transition"
                              >
                                Resume
                              </button>
                            )}
                            {isCompleted && (
                              <span className="text-[11px] font-medium text-[#16a34a]">Seen</span>
                            )}
                            {isCancelled && (
                              <span className="text-[11px] font-medium text-[#94a3b8]">
                                {appt.status === "no_show" ? "No show" : "Cancelled"}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    });

                    // If all appointments were earlier, place Now line at the bottom
                    if (!nowDividerPlaced && !statusFilter && filteredAppointmentsList.length > 0) {
                      items.push(
                        <div key="now-divider-end" className="flex items-center gap-2 px-4 py-1.5">
                          <span className="text-[11px] font-bold text-[#ef4444]">
                            Now {updatedClock}
                          </span>
                          <span className="flex-1 h-px bg-[#fecaca]" />
                        </div>
                      );
                    }

                    return items;
                  })()
                )}
              </div>
            </section>

            {/* ── COLUMN 3: Find Patient & Start Visit ─────────────────────── */}
            <section className="order-3 lg:order-1 xl:order-3 lg:col-span-2 xl:col-span-1 bg-white rounded-2xl border border-[#f1f5f9] shadow-sm overflow-hidden flex flex-col xl:h-[calc(100vh-220px)] xl:min-h-[480px] lg:max-h-[560px]">
              <div className="px-4 py-3 border-b border-[#f1f5f9] flex items-center justify-between gap-2 flex-shrink-0 bg-white">
                <h2 className="text-[14px] font-semibold text-[#334155]">Find patient</h2>
                <button
                  type="button"
                  disabled={patientsDenied}
                  onClick={() => {
                    if (patientsDenied) return;
                    setShowWalkIn((prev) => !prev);
                    setWalkInErr("");
                  }}
                  className={`flex items-center gap-1 text-[12px] font-semibold transition ${
                    patientsDenied
                      ? "text-[#94a3b8] cursor-not-allowed opacity-50"
                      : "text-[#0891b2] hover:text-[#0e7490]"
                  }`}
                >
                  <span className="text-base leading-none font-bold">{showWalkIn ? "×" : "+"}</span>
                  {showWalkIn ? "Close" : "New patient"}
                </button>
              </div>

              {/* Profile scope message & Search bar */}
              <div className="px-4 py-3 border-b border-[#f1f5f9] flex flex-col gap-2.5 flex-shrink-0 bg-white">
                {!lockedDoctorId && doctors.length > 1 ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[12px] text-[#64748b] font-medium">Doctor</span>
                    {doctors.map((d, idx) => {
                      const isPick = (selectedDoctorId || doctors[0]?.doctor_id) === d.doctor_id;
                      return (
                        <button
                          key={d.doctor_id}
                          type="button"
                          onClick={() => setSelectedDoctorId(d.doctor_id)}
                          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-medium border cursor-pointer transition ${
                            isPick
                              ? "border-[#93c5fd] bg-[#eff6ff] text-[#1d4ed8]"
                              : "border-[#e2e8f0] bg-white text-[#475569]"
                          }`}
                        >
                          <span
                            className="w-2 h-2 rounded-[2px] flex-shrink-0"
                            style={{ backgroundColor: DOCTOR_COLORS[idx % DOCTOR_COLORS.length] }}
                          />
                          {d.name.replace(/^Dr\.\s*/i, "")}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-[12px] text-[#94a3b8]">
                    Visits are recorded under your profile · {activeDoctorName}
                  </p>
                )}

                <div className="relative">
                  <svg
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35" />
                  </svg>
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setSelectedPatientId(null);
                    }}
                    placeholder="Search by name, phone or patient ID…"
                    className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-[#e2e8f0] text-[14px] text-[#1e293b] placeholder-[#94a3b8] focus:border-[#93c5fd] focus:ring-2 focus:ring-[#dbeafe] outline-none bg-white"
                  />
                </div>
              </div>

              {/* Inline Walk-In Registration Drawer */}
              {showWalkIn && (
                <form
                  onSubmit={handleRegisterAndStartWalkIn}
                  className="p-4 bg-[#f8fafc] border-b border-[#f1f5f9] flex flex-col gap-3 flex-shrink-0 animate-fadeIn"
                >
                  <div className="text-[13px] font-semibold text-[#334155]">
                    New walk-in patient
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      placeholder="First name *"
                      value={walkInFirst}
                      onChange={(e) => setWalkInFirst(e.target.value)}
                      className="px-3 py-2 rounded-lg border border-[#e2e8f0] text-[14px] text-[#1e293b] bg-white outline-none min-w-0"
                    />
                    <input
                      type="text"
                      placeholder="Last name *"
                      value={walkInLast}
                      onChange={(e) => setWalkInLast(e.target.value)}
                      className="px-3 py-2 rounded-lg border border-[#e2e8f0] text-[14px] text-[#1e293b] bg-white outline-none min-w-0"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      min="0"
                      max="120"
                      placeholder="Age (years) *"
                      value={walkInAge}
                      onChange={(e) => setWalkInAge(e.target.value)}
                      className="px-3 py-2 rounded-lg border border-[#e2e8f0] text-[14px] text-[#1e293b] bg-white outline-none min-w-0"
                    />
                    <div className="flex bg-white border border-[#e2e8f0] rounded-lg p-[3px] gap-[2px]">
                      {(["Male", "Female", "Other"] as const).map((g) => (
                        <button
                          key={g}
                          type="button"
                          onClick={() => setWalkInGender(g)}
                          className={`flex-1 py-1 rounded-md text-[13px] font-medium transition ${
                            walkInGender === g ? "bg-[#eff6ff] text-[#1d4ed8]" : "text-[#64748b] hover:text-[#1e293b]"
                          }`}
                        >
                          {g === "Male" ? "Male" : g === "Female" ? "Female" : "Other"}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center border border-[#e2e8f0] rounded-lg bg-white overflow-hidden">
                    <span className="px-2.5 py-2 text-[14px] text-[#64748b] border-r border-[#e2e8f0] self-stretch flex items-center bg-transparent">
                      +91
                    </span>
                    <input
                      type="tel"
                      placeholder="Mobile number *"
                      value={walkInPhone}
                      onChange={(e) => setWalkInPhone(e.target.value)}
                      className="flex-1 px-3 py-2 text-[14px] text-[#1e293b] outline-none border-0"
                    />
                  </div>

                  {walkInErr && <div className="text-[12px] text-[#dc2626] font-medium">{walkInErr}</div>}

                  <button
                    type="submit"
                    disabled={submittingWalkIn}
                    className="w-full py-2.5 rounded-xl bg-[#2563eb] hover:bg-[#1d4ed8] text-white text-[14px] font-semibold shadow-sm transition disabled:opacity-50"
                  >
                    {submittingWalkIn ? "Registering…" : "Register & Start Visit"}
                  </button>
                </form>
              )}

              {/* Patient List */}
              <div className="overflow-y-auto flex-1 p-2">
                <div className="px-4 pt-2.5 pb-1 text-[11px] font-semibold text-[#94a3b8] uppercase tracking-wider">
                  {query.trim()
                    ? `${filteredPatients.length} match${filteredPatients.length !== 1 ? "es" : ""}`
                    : "Recent patients"}
                </div>

                {loadingPatients || searching ? (
                  <p className="text-xs text-slate-400 text-center py-6">Loading patients…</p>
                ) : patientsDenied ? (
                  <div className="p-4">
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-3 text-center">
                      Your role does not include permission to view patients, so a visit
                      cannot be started. Ask an admin to grant View Patients.
                    </p>
                  </div>
                ) : filteredPatients.length === 0 ? (
                  <div className="flex flex-col items-center justify-center p-8 text-center gap-3">
                    <p className="text-[13px] text-[#94a3b8]">
                      {query.trim() ? `No patients match “${query}”` : "No patients found"}
                    </p>
                    <button
                      type="button"
                      onClick={handleOpenWalkInFromQuery}
                      className="px-3.5 py-2 rounded-lg border border-[#e2e8f0] text-[13px] font-medium text-[#334155] hover:bg-[#f8fafc] transition"
                    >
                      Register as new patient
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-[2px]">
                    {filteredPatients.map((p) => {
                      const pid = p.patient_id as string;
                      const isSelected = selectedPatientId === pid;
                      const fullName = capitalizeWords(`${p.first_name} ${p.last_name}`);
                      const initials = `${p.first_name?.[0] || ""}${p.last_name?.[0] || ""}`.toUpperCase();

                      // Check if patient has a draft visit today or earlier
                      const draft = scopedDraftVisits.find((v) => v.patient_id === pid);
                      // Check if patient has an appointment today
                      const todayAppt = enrichedTodayAppointments.find(
                        (a) => a.patient_id === pid && a.status !== "completed" && a.status !== "cancelled"
                      );

                      const tagText = draft
                        ? toDateStr(new Date(draft.visit_date || draft.created_at)) === todayStr
                          ? "In consult"
                          : "Draft visit"
                        : todayAppt
                        ? `Appt ${todayAppt.slot_start}`
                        : null;

                      const tagColor = draft
                        ? "border-[#fde68a] bg-[#fffbeb] text-[#b45309]"
                        : "border-[#bfdbfe] bg-[#eff6ff] text-[#1d4ed8]";

                      return (
                        <div
                          key={pid ?? `${p.first_name}-${p.last_name}`}
                          className={`rounded-xl border transition-all ${
                            isSelected
                              ? "border-[#bfdbfe] bg-[#f8fbff]"
                              : "border-transparent hover:bg-[#f8fafc]"
                          }`}
                        >
                          <div
                            onClick={() => setSelectedPatientId(isSelected ? null : pid)}
                            className="px-2 py-2.5 flex items-center gap-3 cursor-pointer rounded-xl"
                          >
                            <span className="w-9 h-9 rounded-xl bg-[#eff6ff] text-[#2563eb] flex items-center justify-center font-bold text-[12px] flex-shrink-0">
                              {initials || "PT"}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-[14px] font-semibold text-[#1e293b] truncate">
                                  {fullName}
                                </span>
                                {tagText && (
                                  <span
                                    className={`text-[10px] font-semibold border rounded-full px-[7px] py-[1px] ${tagColor}`}
                                  >
                                    {tagText}
                                  </span>
                                )}
                              </div>
                              <div className="text-[12px] text-[#94a3b8] truncate mt-[1px]">
                                {p.display_id || pid}{formatPatientAgeGender(p.gender, p.date_of_birth, p.age_years) ? ` · ${formatPatientAgeGender(p.gender, p.date_of_birth, p.age_years)}` : ""} · {formatPhoneNumber(p.contact_number)}
                              </div>
                              <div className="text-[12px] text-[#64748b] truncate mt-[1px]">
                                {draft?.chief_complaint
                                  ? `Last visit · ${draft.chief_complaint}`
                                  : "No previous visits"}
                              </div>
                            </div>
                            {isSelected && (
                              <svg className="w-4 h-4 text-[#2563eb] flex-shrink-0 stroke-[2.5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                          </div>

                          {/* Expanded selection actions */}
                          {isSelected && (
                            <div className="px-2 pb-2.5 pt-0 pl-14 flex flex-col gap-1.5">
                              <button
                                type="button"
                                onClick={() => {
                                  if (draft) {
                                    handleContinueVisit(draft);
                                  } else {
                                    handleStartVisitForPatient(p, todayAppt || null);
                                  }
                                }}
                                className={`w-full py-2.5 px-4 rounded-xl text-white text-[14px] font-semibold shadow-sm transition flex items-center justify-center gap-2 ${
                                  draft ? "bg-[#ea580c] hover:opacity-90" : "bg-[#2563eb] hover:opacity-90"
                                }`}
                              >
                                {draft ? "Continue visit" : "Start Visit"}
                                <svg className="w-4 h-4 stroke-[2.5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                </svg>
                              </button>
                              <span className="text-[11px] text-[#94a3b8] text-center">
                                {draft
                                  ? `${VISIT_STEP_LABELS[draft.current_step] || "Diagnosis pad"} · started ${formatTimeFromIsoOrStr(draft.visit_date || draft.created_at)}`
                                  : todayAppt
                                  ? `Linked to today's ${todayAppt.slot_start} appointment · ${activeDoctorName}`
                                  : `Walk-in visit with ${activeDoctorName}`}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>

          </div>
        </div>

        {/* ── Toast with Undo ──────────────────────────────────────────────── */}
        {toastMessage && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 inline-flex items-center gap-2.5 px-4 py-3 rounded-lg bg-white text-[#334155] text-[14px] border-l-4 border-l-[#22c55e] shadow-lg [animation:toastIn_300ms_ease] max-w-[calc(100vw-32px)]">
            <svg className="w-[18px] h-[18px] text-[#22c55e] flex-shrink-0 stroke-[2.5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <circle cx="12" cy="12" r="10" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" />
            </svg>
            <span>{toastMessage}</span>
            {toastUndoAction && (
              <button
                type="button"
                onClick={() => {
                  toastUndoAction();
                  setToastMessage(null);
                  setToastUndoAction(null);
                }}
                className="text-[13px] font-semibold text-[#0891b2] hover:text-[#0e7490] ml-1.5 transition"
              >
                Undo
              </button>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default StartVisitPage;
